import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { entity } from '../../../../sharedComponents/EntitiesDualSelectList';
import { commonActionsReducer } from '../../common/actions';
import { SUBCATEGORIES } from '../../common/categories';
import { TIER_FIELDS } from '../../common/fields';
import { buildField } from '../../common/utils';
import * as actions from './PASActions';

const { RESOURCES, JIT } = SUBCATEGORIES;

export const TIER0 = {
    ID: 'tier0',
    NAME: 'Tier 0',
    TOOLTIP:
        'Tier 0 assets include all identity management systems. Identities/Accounts authenticating to assets in this tier will be considered as tier 0 accounts'
};
export const TIER1 = {
    ID: 'tier1',
    NAME: 'Tier 1',
    TOOLTIP:
        'Tier 1 assets are enterprise assets like servers, databases and applications. Identities/Accounts authenticating to assets in this tier, but not to tier 0 will be considered as tier 1 accounts'
};
export const TIER2 = {
    ID: 'tier2',
    NAME: 'Tier 2',
    TOOLTIP: 'Tier 2 assets include workstations and endpoints, typically accessible by the end user.'
};
const { TIER, DCS_SERVICES } = TIER_FIELDS;


export const fieldsReducer = (state, action) => {
    switch (action.type) {
        case actions.UPDATE_TIER0:
            return updateTier(state, TIER0.ID, action.payload);
        case actions.UPDATE_TIER1:
            return updateTier(state, TIER1.ID, action.payload);
        case actions.UPDATE_TIER2:
            return updateTier(state, TIER2.ID, action.payload);
        case actions.UPDATE_DOMAIN_CONTROLLER_SERVICES:
            return updateDcsServices(state, action.payload);
        case actions.UPDATE_AD_ATTRIBUTES:
            return updateADAttributes(state, action.payload);
        case actions.NOTIFY_DOWNLOAD_SCRIPT:
            return updateDownloadScript(state, action.payload);
        case actions.UPDATE_ACCOUNTS:
            return updateAccounts(state, action.payload);
        case actions.UPDATE_ITSM:
            return updateITSM(state, action.payload);

        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

const updateAccounts = (state, payload) => {
    const newState = { ...state };
    newState.jitCustomSettings.accounts = {
        users: payload.filter((entity) => entity.type === AD_ENTITY_TYPES.USER),
        groups: payload.filter((entity) => entity.type === AD_ENTITY_TYPES.GROUP),
        ous: payload.filter((entity) => entity.type === AD_ENTITY_TYPES.OU)
    };
    return newState;
};

const updateADAttributes = (state, payload) => {
    const newState = { ...state };
    newState.jitCustomSettings.adAttribute = payload.adAttribute;
    newState.jitCustomSettings.adValue = payload.adValue;
    return newState;
};

const updateITSM = (state, payload) => {
    const newState = { ...state };
    newState.jitCustomSettings.itsm = payload.id || '';
    newState.jitCustomSettings.itsmType = payload.type || '';
    return newState;
};

function updateDownloadScript(state, payload) {
    const newState = { ...state };
    newState.jitCustomSettings.breakGlassScriptLastDownload = Date.now();
    return newState;
}

const updateTier = (state, tier, payload) => {
    const newState = { ...state };
    const tierField = newState.tiersConfig[tier].fields[TIER.ID];
    tierField.value = { ...payload.value };
    tierField.isDirty = true;
    return newState;
};

const updateDcsServices = (state, payload) => {
    const newState = { ...state };
    newState.tiersConfig[TIER0.ID].fields[DCS_SERVICES.ID].value = payload.value;
    newState.tiersConfig[TIER0.ID].fields[DCS_SERVICES.ID].isDirty = true;
    return newState;
};

const initAccounts = (accounts: entity[]) => {
    const users = accounts.filter((entity) => entity.type === AD_ENTITY_TYPES.USER);
    const groups = accounts.filter((entity) => entity.type === AD_ENTITY_TYPES.GROUP);
    const ous = accounts.filter((entity) => entity.type === AD_ENTITY_TYPES.OU);

    return {
        users,
        groups,
        ous
    };
};

export const initFieldsState = ({ settings }) => {
    return {
        tiersConfig: {
            [TIER0.ID]: {
                id: TIER0.ID,
                name: TIER0.NAME,
                tooltip: TIER0.TOOLTIP,
                fields: {
                    [TIER.ID]: buildField(TIER, settings.tiersConfig[TIER0.ID]),
                    [DCS_SERVICES.ID]: buildField(DCS_SERVICES, settings.dcsTier0Services)
                },
                validators: {}
            },
            [TIER1.ID]: {
                id: TIER1.ID,
                name: TIER1.NAME,
                tooltip: TIER1.TOOLTIP,
                fields: {
                    [TIER.ID]: buildField(TIER, settings.tiersConfig[TIER1.ID])
                },
                validators: {}
            },
            [TIER2.ID]: {
                id: TIER2.ID,
                name: TIER2.NAME,
                tooltip: TIER2.TOOLTIP,
                fields: {
                    [TIER.ID]: buildField(TIER, settings.tiersConfig[TIER2.ID])
                },
                validators: {}
            }
        },
        jitCustomSettings: {
            adAttribute: settings.jitCustomSettings?.adAttribute,
            adValue: settings.jitCustomSettings?.adValue,
            breakGlassScriptLastDownload: settings.jitCustomSettings?.breakGlassScriptLastDownload,
            accounts: initAccounts(settings.jitCustomSettings?.accounts || []),
            itsm: settings.jitCustomSettings?.itsm || '',
            itsmType: settings.jitCustomSettings?.itsmType || ''
        }
    };
};
const parseTierPartEntities = (entities) => {
    const parseEntities = {};
    for (const guid in entities) {
        const entity = entities[guid];
        parseEntities[guid] = { services: entity.services, protocols: entity.protocols };
    }
    return parseEntities;
};

const parseTierPart = (tierPart) => ({
    servers: parseTierPartEntities(tierPart.servers) ?? {},
    groups: parseTierPartEntities(tierPart.groups) ?? {},
    ous: parseTierPartEntities(tierPart.ous) ?? {},
    ips: tierPart.ips ?? {},
    conditions: tierPart.conditions ?? []
});

const parseTier = (tier) => {
    return {
        excluded: parseTierPart(tier.excluded),
        included: parseTierPart(tier.included)
    };
};

export const mapToSettings = (saveAll, selectedTab, fields) => {
    if (selectedTab === RESOURCES.ID) {
        const tier0Config = { ...fields.tiersConfig[TIER0.ID].fields.tier.value };
        const tier1Config = { ...fields.tiersConfig[TIER1.ID].fields.tier.value };
        const tier2Config = { ...fields.tiersConfig[TIER2.ID].fields.tier.value };

        return {
            tiersConfig: {
                [TIER0.ID]: parseTier(tier0Config),
                [TIER1.ID]: parseTier(tier1Config),
                [TIER2.ID]: parseTier(tier2Config)
            },
            dcsTier0Services: fields.tiersConfig[TIER0.ID].fields[DCS_SERVICES.ID].value
        };
    } else if (selectedTab === JIT.ID) {
        return {
            jitCustomSettings: {
                adAttribute: fields.jitCustomSettings.adAttribute,
                adValue: fields.jitCustomSettings.adValue,
                breakGlassScriptLastDownload: fields.jitCustomSettings.breakGlassScriptLastDownload,
                accounts: [
                    ...fields.jitCustomSettings.accounts.users.map((user) => ({ ...user, type: AD_ENTITY_TYPES.USER })),
                    ...fields.jitCustomSettings.accounts.groups.map((group) => ({
                        ...group,
                        type: AD_ENTITY_TYPES.GROUP
                    })),
                    ...fields.jitCustomSettings.accounts.ous.map((ou) => ({ ...ou, type: AD_ENTITY_TYPES.OU }))
                ],
                itsm: fields.jitCustomSettings.itsm || '',
                itsmType: fields.jitCustomSettings.itsmType || ''
            }
        };
    }
    return {};
};
